import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import { WORKS } from "../data/site";

/** Only show featured projects in the stacking showcase */
const FEATURED = WORKS.filter((w) => w.featured);

/* ─────────────────────────── Card ─────────────────────────── */
function Card({ work, i, progress, range, targetScale }) {
  const container = useRef(null);

  // This scroll tracks when THIS card enters the viewport (for the image zoom)
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start end", "start start"],
  });

  // Image zooms out from 1.35 → 1 as the card enters the view
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.35, 1]);

  // The card shrinks slightly as subsequent cards stack on top of it
  const scale = useTransform(progress, range, [1, targetScale]);

  return (
    <div
      ref={container}
      className="min-h-screen flex items-center justify-center sticky top-0 px-4 py-8 sm:px-6"
    >
      <motion.div
        style={{
          scale,
          // Each card peeks up slightly higher than the previous
          top: `calc(-5vh + ${i * 28}px)`,
          background: "rgba(255, 255, 255, 0.93)",
          boxShadow:
            "0 50px 100px -24px rgba(11,46,94,0.28), 0 1px 0 rgba(255,255,255,0.9) inset",
          border: "1px solid rgba(11, 46, 94, 0.07)",
        }}
        className="relative flex h-[min(640px,calc(100svh-96px))] w-full max-w-[25rem] flex-col rounded-[1.5rem] overflow-hidden origin-top sm:max-w-6xl md:h-[580px] md:w-[92%] md:flex-row md:rounded-3xl"
      >
        {/* Coloured left accent stripe */}
        <div
          className="absolute left-0 top-0 bottom-0 w-[4px] z-10"
          style={{ background: work.accent }}
        />

        {/* ── LEFT — Text content ── */}
        <div className="relative order-2 flex min-h-0 flex-1 flex-col justify-between px-6 py-6 sm:px-8 md:order-1 md:w-[40%] md:flex-none md:px-12 md:py-14">
          {/* Index label */}
          <div
            className="absolute top-5 right-5 font-mono text-[10px] tracking-[0.18em] uppercase opacity-30 text-[#0b2e5e] md:top-8 md:right-6 md:text-[11px] md:tracking-[0.22em]"
          >
            {String(i + 1).padStart(2, "0")} / {String(FEATURED.length).padStart(2, "0")}
          </div>

          <div>
            {/* Category tags */}
            <div className="flex flex-wrap gap-2 mb-4 md:mb-7">
              {work.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[9px] tracking-[0.14em] uppercase font-medium px-2.5 py-1 rounded-full md:text-[10px] md:tracking-[0.18em] md:px-3"
                  style={{
                    background: `${work.accent}16`,
                    border: `1px solid ${work.accent}40`,
                    color: work.accent,
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Title */}
            <h3 className="font-display text-[1.8rem] leading-[1.02] text-[#0b2e5e] tracking-tight sm:text-[2rem] md:text-[2.6rem] md:leading-[1.08]">
              {work.title}
            </h3>

            {/* Client · Year */}
            <div className="flex items-center gap-2 mt-4">
              <span
                className="h-[6px] w-[6px] rounded-full flex-shrink-0"
                style={{ background: work.accent }}
              />
              <span className="text-[#0b2e5e]/50 text-xs font-medium tracking-wide sm:text-sm">
                {work.client} &nbsp;·&nbsp; {work.year}
              </span>
            </div>

            {/* Description */}
            <p className="mt-4 text-[#0b2e5e]/58 text-sm leading-relaxed max-w-none line-clamp-3 md:mt-6 md:max-w-[280px] md:text-[15px]">
              {work.description}
            </p>
          </div>

          {/* CTA */}
          <Link
            to="/works"
            className="inline-flex items-center gap-2 group w-fit mt-5 md:mt-8"
          >
            <span
              className="text-sm font-semibold border-b transition-all duration-300 pb-0.5 group-hover:pb-1"
              style={{ color: work.accent, borderColor: `${work.accent}50` }}
            >
              See project
            </span>
            <ArrowUpRight
              size={15}
              weight="bold"
              style={{ color: work.accent }}
              className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"
            />
          </Link>
        </div>

        {/* ── RIGHT — Image with zoom effect ── */}
        <div className="relative order-1 h-[46%] min-h-[230px] overflow-hidden md:order-2 md:h-auto md:w-[60%] md:flex-none">
          <motion.div
            className="absolute inset-0"
            style={{ scale: imageScale }}
          >
            {work.img ? (
              <img
                src={work.img}
                alt={work.title}
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              /* Placeholder when images aren't placed yet */
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${work.accent}28 0%, ${work.accent}08 100%)`,
                }}
              >
                <span
                  className="font-display select-none font-semibold tracking-tighter opacity-[0.08]"
                  style={{ fontSize: "clamp(5rem, 18vw, 14rem)", color: work.accent }}
                >
                  {work.client.charAt(0)}
                </span>
              </div>
            )}

            {/* Edge gradient so it blends into the card */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-transparent pointer-events-none" />
          </motion.div>

          {/* Year badge */}
          <div
            className="absolute top-4 right-4 z-10 text-[9px] font-mono tracking-[0.16em] uppercase px-2.5 py-1.5 rounded-full text-white md:top-6 md:right-6 md:text-[10px] md:tracking-[0.18em] md:px-3"
            style={{
              background: "rgba(11, 23, 42, 0.52)",
              border: "1px solid rgba(255,255,255,0.22)",
              backdropFilter: "blur(10px)",
            }}
          >
            {work.year}
          </div>

          {/* Soft accent glow on corner */}
          <div
            className="absolute -bottom-12 -right-12 w-56 h-56 rounded-full blur-3xl opacity-20 pointer-events-none"
            style={{ background: work.accent }}
          />
        </div>
      </motion.div>
    </div>
  );
}

/* ─────────────────────────── Container ─────────────────────── */
export default function StackedCards() {
  const container = useRef(null);

  // Master scroll progress for the entire stacking section
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start start", "end end"],
  });

  return (
    /**
     * The container needs to be tall enough for each card
     * to have its own scroll "lane" — 100vh per card.
     */
    <div
      ref={container}
      style={{ height: `${FEATURED.length * 100}vh` }}
    >
      {FEATURED.map((work, i) => {
        /**
         * targetScale: the final scale of this card after all
         * subsequent cards have stacked on top of it.
         * Cards shrink by 4% per layer above them.
         */
        const targetScale = 1 - (FEATURED.length - i) * 0.04;
        return (
          <Card
            key={work.id}
            work={work}
            i={i}
            progress={scrollYProgress}
            range={[i / FEATURED.length, 1]}
            targetScale={targetScale}
          />
        );
      })}
    </div>
  );
}
